import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { View, Text, TouchableOpacity, ScrollView, TextInput, ActivityIndicator, Platform } from 'react-native';
import { erFetch, getBase, ENTITY_TYPES, LEAGUE_BASELINE, STATUS_OPTIONS, isSelfExplanatoryQuery, st } from './entityResolutionConstants';
import { ResCard, CorrectionForm, BulkBar, SelectAllRow, QueryTypeLegend, QueryTester, MetricsView } from './entityResolutionParts';
import { REVIEW_QUEUE_STATUSES, FlagReasons, FlaggedCandidates, PendingAliasList } from './entityResolutionReview';
import { Ionicons } from '@expo/vector-icons';
import { uiVar } from './floodlight/uiStyleCss';



export default function EntityResolutionTab() {
  const [resolutions, setResolutions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeView, setActiveView] = useState('review');
  const [dropdownData, setDropdownData] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selected, setSelected] = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [addForm, setAddForm] = useState({ input: '', type: 'player', sport: '', sports: [], league: '', leagues: [], team: '', teams: [], away_team: '', player: '', players: [], market: '', markets: [], line: '', direction: '', sportsbooks: [], queryTypes: [] });
  const [addLoading, setAddLoading] = useState(false);
  const [aiEnriching, setAiEnriching] = useState(false);
  const [aiEnrichCount, setAiEnrichCount] = useState(0);
  const [highlightQuery, setHighlightQuery] = useState(null);
  const [mining, setMining] = useState(false);
  const [mineResult, setMineResult] = useState(null);
  const [flaggedQueries, setFlaggedQueries] = useState([]);
  const [flaggedLoading, setFlaggedLoading] = useState(false);
  const [flaggedSelected, setFlaggedSelected] = useState(new Set());
  const [candidateBusy, setCandidateBusy] = useState(null);   // { id, index } while confirming
  const [pendingAliases, setPendingAliases] = useState([]);
  const [aliasesLoading, setAliasesLoading] = useState(false);
  const [aliasBusy, setAliasBusy] = useState(null);
  const fetchResolutions = useCallback(async (status, type) => {
    setLoading(true); setError('');
    try {
      const base = getBase();
      if (!base) throw new Error('Functions base URL not configured');
      const params = new URLSearchParams({ action: 'list', limit: '500' });
      if (status) params.set('status', status);
      if (type) params.set('type', type);
      const r = await erFetch(`${base}/entityResolutions?${params}`);
      const j = await r.json();
      if (j.success) { setResolutions(j.resolutions || []); setSelected(new Set()); }
      else throw new Error(j.error || 'Failed');
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const fetchStats = useCallback(async () => {
    try { const r = await erFetch(`${getBase()}/entityResolutions?action=stats`); const j = await r.json(); if (j.success) setStats(j.stats); } catch (_) {}
  }, []);

  const fetchDropdownData = useCallback(async () => {
    try {
      const r = await erFetch(`${getBase()}/entityResolutions?action=getDropdownData`);
      const j = await r.json();
      if (j.success) {
        const leagues = new Set(j.leagues || []);
        const sports = new Set(j.sports || []);
        const l2s = { ...(j.leagueToSport || {}) };
        for (const [league, sport] of Object.entries(LEAGUE_BASELINE)) {
          leagues.add(league);
          sports.add(sport);
          l2s[league.toLowerCase()] = sport.toLowerCase();
        }
        setDropdownData({ ...j, leagues: [...leagues].sort(), sports: [...sports].sort(), leagueToSport: l2s });
      }
    } catch (_) {}
  }, []);

  const fetchReviewQueue = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const base = getBase();
      if (!base) throw new Error('Functions base URL not configured');
      // S2: pending (LLM-learned, mined, flagged), legacy auto, and user reports.
      const lists = await Promise.all(REVIEW_QUEUE_STATUSES.map(status =>
        erFetch(`${base}/entityResolutions?action=list&status=${status}&limit=500`).then(r => r.json())));
      const merged = lists.flatMap(l => l.resolutions || []);
      merged.sort((a, b) => (b.created_at?._seconds || 0) - (a.created_at?._seconds || 0));
      setResolutions(merged); setSelected(new Set());

      // Trigger AI enrichment for items missing suggestions (cap at 25 per batch to avoid timeout)
      const needsAI = merged.filter(r => !r.ai_suggestion).map(r => r.id).slice(0, 25);
      if (needsAI.length > 0) {
        setAiEnriching(true);
        setAiEnrichCount(needsAI.length);
        erFetch(`${base}/entityResolutions`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'enrichSuggestions', docIds: needsAI })
        }).then(r => r.json()).then(j => {
          // Re-fetch to get updated suggestions regardless
          Promise.all(REVIEW_QUEUE_STATUSES.map(status =>
            erFetch(`${base}/entityResolutions?action=list&status=${status}&limit=500`).then(r => r.json())
          )).then((lists) => {
            const updated = lists.flatMap(l => l.resolutions || []);
            updated.sort((x, y) => (y.created_at?._seconds || 0) - (x.created_at?._seconds || 0));
            setResolutions(updated);
          });
        }).catch(() => {}).finally(() => { setAiEnriching(false); setAiEnrichCount(0); });
      }
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  const fetchFlagged = useCallback(async () => {
    setFlaggedLoading(true);
    try {
      const base = getBase();
      const r = await erFetch(`${base}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listFlagged', limit: 200 })
      });
      const j = await r.json();
      if (j.success) { setFlaggedQueries(j.flagged || []); setFlaggedSelected(new Set()); }
    } catch (_) {}
    finally { setFlaggedLoading(false); }
  }, []);

  const handlePromoteFlagged = async (ids) => {
    try {
      const base = getBase();
      const r = await erFetch(`${base}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'promoteFlagged', docIds: ids })
      });
      const j = await r.json();
      if (j.success) { fetchFlagged(); fetchStats(); fetchReviewQueue(); }
    } catch (_) {}
  };

  // S2: one click on a flagged query's candidate confirms it for everyone.
  const handleUseCandidate = async (fq, index) => {
    setCandidateBusy({ id: fq.id, index });
    try {
      const r = await erFetch(`${getBase()}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'confirmFlaggedCandidate', docId: fq.id, candidateIndex: index,
          candidateName: fq.candidates?.[index]?.name, candidateType: fq.candidates?.[index]?.type })
      });
      const j = await r.json();
      if (j.success) {
        const cand = fq.candidates?.[index];
        setFlaggedQueries(prev => prev.map(x => x.id === fq.id
          ? { ...x, promoted: true, resolved_candidate: cand ? { type: cand.type, name: cand.name } : x.resolved_candidate } : x));
        fetchStats();
      } else setError(j.error || 'Could not confirm');
    } catch (e) { setError(e.message); }
    finally { setCandidateBusy(null); }
  };

  const fetchPendingAliases = useCallback(async () => {
    setAliasesLoading(true);
    try {
      const r = await erFetch(`${getBase()}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'listMarketAliases', status: 'pending_review' })
      });
      const j = await r.json();
      if (j.success) setPendingAliases(j.aliases || []);
      else setError(j.error || 'Could not load aliases');
    } catch (e) { setError(e.message); }
    finally { setAliasesLoading(false); }
  }, []);

  const handleReviewAlias = async (aliasId, status) => {
    setAliasBusy(aliasId);
    try {
      const r = await erFetch(`${getBase()}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'reviewMarketAlias', aliasId, status })
      });
      const j = await r.json();
      if (j.success) setPendingAliases(prev => prev.filter(a => a.id !== aliasId));
      else setError(j.error || 'Could not review alias');
    } catch (e) { setError(e.message); }
    finally { setAliasBusy(null); }
  };

  const handleDismissFlagged = async (ids) => {
    try {
      const base = getBase();
      const r = await erFetch(`${base}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'dismissFlagged', docIds: ids })
      });
      const j = await r.json();
      if (j.success) { fetchFlagged(); fetchStats(); }
    } catch (_) {}
  };

  useEffect(() => { fetchReviewQueue(); fetchStats(); fetchDropdownData(); }, []);
  useEffect(() => {
    if (activeView === 'review') fetchReviewQueue();
    else if (activeView === 'flagged') fetchFlagged();
    else if (activeView === 'aliases') fetchPendingAliases();
    else fetchResolutions(filterStatus, filterType);
  }, [filterStatus, filterType, activeView]);

  const handleReview = async (docId, status, extra = {}) => {
    try {
      const r = await erFetch(`${getBase()}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'review', docId, status, ...extra })
      });
      const j = await r.json();
      if (j.success) {
        // In review queue, remove the card. In all resolutions, update it in place.
        if (activeView === 'review') {
          setResolutions(prev => prev.filter(x => x.id !== docId));
        } else {
          setResolutions(prev => prev.map(x => x.id === docId ? { ...x, status, ...(extra.resolved_name ? { resolved_name: extra.resolved_name } : {}), ...(extra.sport !== undefined ? { sport: extra.sport } : {}), ...(extra.league !== undefined ? { league: extra.league } : {}), metadata: { ...(x.metadata || {}), sports: extra.resolved_sports ?? x.metadata?.sports, leagues: extra.resolved_leagues ?? x.metadata?.leagues, team: extra.resolved_team ?? x.metadata?.team, teams: extra.resolved_teams ?? x.metadata?.teams, away_team: extra.resolved_away_team ?? x.metadata?.away_team, player: extra.resolved_player ?? x.metadata?.player, players: extra.resolved_players ?? x.metadata?.players, market: extra.resolved_market ?? x.metadata?.market, markets: extra.resolved_markets ?? x.metadata?.markets, line: extra.resolved_line ?? x.metadata?.line, direction: extra.resolved_direction ?? x.metadata?.direction, sportsbooks: extra.resolved_sportsbooks ?? x.metadata?.sportsbooks, queryTypes: extra.resolved_query_types ?? x.metadata?.queryTypes } } : x));
        }
        setSelected(prev => { const n = new Set(prev); n.delete(docId); return n; });
        setEditingId(null); fetchStats();
      }
    } catch (e) { setError(e.message); }
  };

  const handleSaveCorrection = async (docId) => {
    const players = editForm.players?.length ? editForm.players : (editForm.player ? [editForm.player] : []);
    const teams = editForm.teams?.length ? editForm.teams : (editForm.team ? [editForm.team] : []);
    const markets = editForm.markets?.length ? editForm.markets : (editForm.market ? [editForm.market] : []);
    const sports = editForm.sports?.length ? editForm.sports : (editForm.sport ? [editForm.sport] : []);
    const leagues = editForm.leagues?.length ? editForm.leagues : (editForm.league ? [editForm.league] : []);
    const resolvedName = players[0] || teams[0] || markets[0] || '';
    // Auto-infer type from what's filled
    const type = players.length > 0 ? 'player' : teams.length > 0 ? 'team' : markets.length > 0 ? 'market' : leagues.length > 0 ? 'league' : 'market';
    await handleReview(docId, 'corrected', {
      resolved_name: resolvedName, type,
      sport: sports[0] || '', league: leagues[0] || '',
      resolved_sports: sports.length > 0 ? sports : undefined,
      resolved_leagues: leagues.length > 0 ? leagues : undefined,
      resolved_team: teams[0] || '', resolved_away_team: editForm.away_team,
      resolved_teams: teams.length > 1 ? teams : undefined,
      resolved_player: players[0] || '',
      resolved_players: players.length > 1 ? players : undefined,
      resolved_market: markets[0] || '',
      resolved_markets: markets.length > 0 ? markets : undefined,
      resolved_line: editForm.line,
      resolved_direction: editForm.direction,
      resolved_sportsbooks: editForm.sportsbooks || [],
      resolved_query_types: editForm.queryTypes || [],
    });
  };

  const handleAdd = async () => {
    const players = addForm.players?.length ? addForm.players : (addForm.player ? [addForm.player] : []);
    const teams = addForm.teams?.length ? addForm.teams : (addForm.team ? [addForm.team] : []);
    const markets = addForm.markets?.length ? addForm.markets : (addForm.market ? [addForm.market] : []);
    const sports = addForm.sports?.length ? addForm.sports : (addForm.sport ? [addForm.sport] : []);
    const leagues = addForm.leagues?.length ? addForm.leagues : (addForm.league ? [addForm.league] : []);
    const resolvedName = players[0] || teams[0] || markets[0] || '';
    const type = players.length > 0 ? 'player' : teams.length > 0 ? 'team' : markets.length > 0 ? 'market' : 'market';
    if (!addForm.input || !resolvedName) return;
    setAddLoading(true);
    try {
      const r = await erFetch(`${getBase()}/entityResolutions`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'add', ...addForm, type, resolved_name: resolvedName, sport: sports[0] || '', league: leagues[0] || '', resolved_sports: sports, resolved_leagues: leagues, resolved_sportsbooks: addForm.sportsbooks || [], resolved_query_types: addForm.queryTypes || [] })
      });
      const j = await r.json();
      if (j.success) {
        setAddForm({ input: '', type: 'player', sport: '', sports: [], league: '', leagues: [], team: '', teams: [], away_team: '', player: '', players: [], market: '', markets: [], line: '', direction: '', sportsbooks: [], queryTypes: [] });
        fetchResolutions(activeView === 'review' ? 'pending' : filterStatus, filterType); fetchStats();
      }
    } catch (e) { setError(e.message); }
    finally { setAddLoading(false); }
  };

  const handleBulk = async (action) => {
    if (selected.size === 0) return; setBulkLoading(true);
    for (const docId of [...selected]) { try { await handleReview(docId, action); } catch (_) {} }
    setSelected(new Set()); setBulkLoading(false);
    if (activeView === 'review') fetchReviewQueue(); else fetchResolutions(filterStatus, filterType);
    fetchStats();
  };

  const toggleSelect = id => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleSelectAll = () => selected.size === filtered.length ? setSelected(new Set()) : setSelected(new Set(filtered.map(r => r.id)));

  const filtered = useMemo(() => {
    const pool = activeView === 'review'
      ? resolutions.filter(r => !isSelfExplanatoryQuery(r.input_term))
      : resolutions;
    if (!searchTerm) return pool;
    const q = searchTerm.toLowerCase();
    return pool.filter(r => (r.input_term || '').toLowerCase().includes(q) || (r.resolved_name || '').toLowerCase().includes(q));
  }, [resolutions, searchTerm, activeView]);

  const startEdit = (r) => {
    setEditingId(r.id);
    const m = r.metadata || {};
    const sport = r.sport || m.sport || '';
    const league = r.league || m.league || '';
    const sports = m.sports?.length > 0 ? [...m.sports] : (sport ? [sport] : []);
    const leagues = m.leagues?.length > 0 ? [...m.leagues] : (league ? [league] : []);
    const team = m.team || '';
    const away_team = m.away_team || '';
    const player = m.player || (r.type === 'player' ? r.resolved_name : '') || '';
    const market = m.market || (r.type === 'market' ? r.resolved_name : '') || '';
    const markets = m.markets?.length > 0 ? [...m.markets] : (market ? [market] : []);
    const teams = m.teams?.length > 0 ? [...m.teams] : (team ? [team] : []);
    const players = m.players?.length > 0 ? [...m.players] : (player ? [player] : []);
    const autoType = players.length > 0 ? 'player' : teams.length > 0 ? 'team' : markets.length > 0 ? 'market' : r.type || 'market';
    setEditForm({
      type: autoType,
      sport, sports, league, leagues, team, teams, away_team, player, players, market, markets,
      line: m.line != null ? String(m.line) : '', direction: m.direction || '',
      sportsbooks: m.sportsbooks?.length > 0 ? [...m.sportsbooks] : [],
      queryTypes: m.queryTypes?.length > 0 ? [...m.queryTypes] : [],
    });
  };

  const allSelected = filtered.length > 0 && selected.size === filtered.length;

  return (
    <ScrollView style={st.wrap} contentContainerStyle={st.inner}>
      {stats && (
        <View style={st.statsRow}>
          {[['Total', stats.total, '#fff', '', 'all'], ['Pending', (stats.by_status?.pending || 0) + (stats.by_status?.auto || 0), '#f59e0b', '', 'review'], ['Confirmed', stats.by_status?.confirmed || 0, '#10b981', 'confirmed', 'all'], ['Rejected', stats.by_status?.rejected || 0, '#ef4444', 'rejected', 'all'], ['Flagged', stats.flagged_queries || 0, '#38bdf8', '', 'flagged'], ['LLM Saved', stats.total_use_count, '#a78bfa', '', '']].map(([l, v, c, statusKey, targetView]) => (
            <TouchableOpacity key={l} style={st.statCard} onPress={() => { if (targetView) { setFilterStatus(statusKey); setActiveView(targetView); } }} activeOpacity={0.7}>
              <Text style={[st.statVal, { color: c }]}>{v ?? 0}</Text><Text style={st.statLbl}>{l}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
      <View style={st.tabs}>
        {[['review','Review Queue'],['flagged','Flagged Queries'],['aliases','Market Aliases'],['all','All Resolutions'],['tester','Query Tester'],['add','Manual Add'],['metrics','Metrics']].map(([k,l]) => (
          <TouchableOpacity key={k} style={[st.tab, activeView === k && st.tabOn]} onPress={() => setActiveView(k)}>
            <Text style={[st.tabTxt, activeView === k && st.tabTxtOn]}>{l}</Text>
          </TouchableOpacity>
        ))}
      </View>
      <QueryTypeLegend />
      {error ? <Text style={st.errTxt}>{error}</Text> : null}

      {(activeView === 'review' || activeView === 'all') && (
        <View>
          <Text style={st.title}>{activeView === 'review' ? `Review Queue (${filtered.length})` : `All Resolutions (${filtered.length})`}</Text>
          {activeView === 'all' && (
            <>
              <View style={st.filterRow}>
                <Text style={st.filterLbl}>Status:</Text>
                {['', ...STATUS_OPTIONS].map(s => (
                  <TouchableOpacity key={s||'all'} style={[st.chip, filterStatus === s && st.chipOn]} onPress={() => setFilterStatus(s)}>
                    <Text style={[st.chipTxt, filterStatus === s && st.chipTxtOn]}>{s || 'All'}</Text>
                  </TouchableOpacity>
                ))}
              </View>
              <View style={st.filterRow}>
                <Text style={st.filterLbl}>Type:</Text>
                {['', ...ENTITY_TYPES].map(t => (
                  <TouchableOpacity key={t||'all'} style={[st.chip, filterType === t && st.chipOn]} onPress={() => setFilterType(t)}>
                    <Text style={[st.chipTxt, filterType === t && st.chipTxtOn]}>{t || 'All'}</Text>
                  </TouchableOpacity>
                ))}
              </View>
            </>
          )}
          <TextInput style={st.search} placeholder="Search resolutions..." placeholderTextColor="rgba(255,255,255,0.3)" value={searchTerm} onChangeText={setSearchTerm} />
          {aiEnriching || mining ? (
            <View style={st.aiAnalyzing}>
              <ActivityIndicator size="small" color={mining ? '#38bdf8' : '#a78bfa'} />
              <Text style={st.aiAnalyzingTxt}>{mining ? 'Mining analytics for problem queries...' : `AI is analyzing ${aiEnrichCount} items...`}</Text>
            </View>
          ) : activeView === 'review' ? (
            <View style={{ flexDirection: 'row', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
              {filtered.length > 0 && (
                <TouchableOpacity style={st.aiRerunBtn} onPress={() => {
                  const ids = filtered.map(r => r.id);
                  setAiEnriching(true); setAiEnrichCount(ids.length);
                  const base = getBase();
                  erFetch(`${base}/entityResolutions`, {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'enrichSuggestions', docIds: ids, force: true })
                  }).then(r => r.json()).then(() => fetchReviewQueue()).catch(() => {}).finally(() => { setAiEnriching(false); setAiEnrichCount(0); });
                }}>
                  <Ionicons name="sparkles" size={13} color={uiVar('adm-accent', '#a78bfa')} />
                  <Text style={st.aiRerunTxt}>Re-run AI on all ({filtered.length})</Text>
                </TouchableOpacity>
              )}
              <TouchableOpacity style={[st.aiRerunBtn, { borderColor: '#38bdf8' }]} onPress={() => {
                setMining(true); setMineResult(null);
                const base = getBase();
                erFetch(`${base}/entityResolutions`, {
                  method: 'POST', headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ action: 'mineAnalytics', days: 7, minOccurrences: 2, maxPromotions: 50 })
                }).then(r => r.json()).then(j => {
                  setMineResult(j);
                  if (j.promoted > 0) fetchReviewQueue();
                }).catch(e => setMineResult({ error: e.message })).finally(() => setMining(false));
              }}>
                <Ionicons name="analytics" size={13} color="#38bdf8" />
                <Text style={[st.aiRerunTxt, { color: '#38bdf8' }]}>Mine Analytics</Text>
              </TouchableOpacity>
            </View>
          ) : null}
          {mineResult && !mineResult.error && (
            <View style={{ backgroundColor: 'rgba(56,189,248,0.1)', borderRadius: 8, padding: 10, marginBottom: 8, borderWidth: 1, borderColor: 'rgba(56,189,248,0.3)' }}>
              <Text style={{ color: '#38bdf8', fontWeight: '600', fontSize: 13, marginBottom: 4 }}>
                Analytics Mining Complete
              </Text>
              <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                Scanned {mineResult.scanned ?? 0} queries · Found {mineResult.problemQueries ?? 0} problem patterns · Promoted {mineResult.promoted ?? 0} to review · Skipped {mineResult.skipped ?? 0} (already tracked)
              </Text>
              {mineResult.topProblems?.length > 0 && (
                <View style={{ marginTop: 6 }}>
                  <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, marginBottom: 2 }}>Top problem queries:</Text>
                  {mineResult.topProblems.slice(0, 5).map((p, i) => (
                    <Text key={i} style={{ color: 'rgba(255,255,255,0.7)', fontSize: 11 }}>
                      {`"${p.query}" — ${p.zeroResults}/${p.occurrences} failed, avg conf ${p.avgConfidence}`}
                    </Text>
                  ))}
                </View>
              )}
              <TouchableOpacity onPress={() => setMineResult(null)} style={{ marginTop: 6 }}>
                <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Dismiss</Text>
              </TouchableOpacity>
            </View>
          )}
          {mineResult?.error && (
            <View style={{ backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: 8, padding: 10, marginBottom: 8, borderWidth: 1, borderColor: 'rgba(239,68,68,0.3)' }}>
              <Text style={{ color: '#ef4444', fontSize: 12 }}>Mining error: {mineResult.error}</Text>
              <TouchableOpacity onPress={() => setMineResult(null)} style={{ marginTop: 4 }}>
                <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>Dismiss</Text>
              </TouchableOpacity>
            </View>
          )}
          {selected.size > 0 && <BulkBar count={selected.size} onApprove={() => handleBulk('confirmed')} onReject={() => handleBulk('rejected')} loading={bulkLoading} />}
          {loading ? <ActivityIndicator color="#fff" style={{ marginTop: 24 }} /> : (
            <>
              {filtered.length > 0 && <SelectAllRow all={allSelected} onToggle={toggleSelectAll} count={filtered.length} />}
              {filtered.length === 0 && <Text style={st.emptyTxt}>{activeView === 'review' ? 'No pending resolutions' : 'No resolutions found'}</Text>}
              {filtered.map(r => (
                <ResCard key={r.id} r={r} sel={selected.has(r.id)} onSel={() => toggleSelect(r.id)}
                  highlighted={highlightQuery && r.input_term?.toLowerCase() === highlightQuery?.toLowerCase()}
                  editing={editingId === r.id} form={editForm} setForm={setEditForm} dd={dropdownData}
                  onApprove={() => handleReview(r.id, 'confirmed')}
                  onReject={() => handleReview(r.id, 'rejected')}
                  onEdit={() => startEdit(r)}
                  onCancel={() => setEditingId(null)}
                  onSave={() => handleSaveCorrection(r.id)}
                  onAcceptSuggestion={(sug) => {
                    setEditingId(r.id);
                    const toA = (v) => Array.isArray(v) ? v : (v ? [v] : []);
                    const sugSports = toA(sug.sport);
                    const sugLeagues = toA(sug.league);
                    const sugTeams = toA(sug.team);
                    const sugPlayers = toA(sug.player);
                    const sugMarkets = toA(sug.market);
                    const sugQueryTypes = toA(sug.queryTypes).filter(t => ['SGP','LIVE','STRAIGHT'].includes(t));
                    const sugSportsbooks = toA(sug.sportsbooks);
                    setEditForm({
                      type: r.type || 'market',
                      sport: sugSports[0] || '', sports: sugSports,
                      league: sugLeagues[0] || '', leagues: sugLeagues,
                      team: sugTeams[0] || '', teams: sugTeams,
                      away_team: sug.away_team || '',
                      player: sugPlayers[0] || '', players: sugPlayers,
                      market: sugMarkets[0] || '', markets: sugMarkets,
                      line: sug.line != null ? String(sug.line) : '',
                      direction: sug.direction || '',
                      queryTypes: sugQueryTypes,
                      sportsbooks: sugSportsbooks,
                    });
                  }} />
              ))}
            </>
          )}
        </View>
      )}

      {activeView === 'flagged' && (
        <View>
          <Text style={st.title}>Flagged Queries ({flaggedQueries.length})</Text>
          <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 12, lineHeight: 18 }}>
            Searches the system was unsure about: 0 results, low confidence, several players or teams matching, an AI guess, or a saved answer a check rejected. Pick what the person meant (applies to everyone), promote to the review queue, or dismiss.
          </Text>
          {flaggedSelected.size > 0 && (
            <View style={{ flexDirection: 'row', gap: 8, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
              <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 13 }}>{flaggedSelected.size} selected</Text>
              <TouchableOpacity style={{ backgroundColor: '#10b981', borderRadius: 6, paddingHorizontal: 12, paddingVertical: 6 }}
                onPress={() => handlePromoteFlagged([...flaggedSelected])}>
                <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 12, fontWeight: '600' }}>Promote to Review</Text>
              </TouchableOpacity>
              <TouchableOpacity style={{ backgroundColor: '#ef4444', borderRadius: 6, paddingHorizontal: 12, paddingVertical: 6 }}
                onPress={() => handleDismissFlagged([...flaggedSelected])}>
                <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 12, fontWeight: '600' }}>Dismiss</Text>
              </TouchableOpacity>
              <TouchableOpacity onPress={() => setFlaggedSelected(new Set())}>
                <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Clear selection</Text>
              </TouchableOpacity>
            </View>
          )}
          {flaggedLoading ? <ActivityIndicator color="#fff" style={{ marginTop: 24 }} /> : (
            <>
              {flaggedQueries.length > 0 && (
                <TouchableOpacity style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 8 }}
                  onPress={() => {
                    if (flaggedSelected.size === flaggedQueries.length) setFlaggedSelected(new Set());
                    else setFlaggedSelected(new Set(flaggedQueries.map(f => f.id)));
                  }}>
                  <View style={{ width: 18, height: 18, borderRadius: 4, borderWidth: 1.5, borderColor: flaggedSelected.size === flaggedQueries.length ? '#38bdf8' : 'rgba(255,255,255,0.3)', backgroundColor: flaggedSelected.size === flaggedQueries.length ? '#38bdf8' : 'transparent', justifyContent: 'center', alignItems: 'center' }}>
                    {flaggedSelected.size === flaggedQueries.length && <Ionicons name="checkmark" size={12} color={uiVar('adm-ink', '#fff')} />}
                  </View>
                  <Text style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>Select all {flaggedQueries.length}</Text>
                </TouchableOpacity>
              )}
              {flaggedQueries.length === 0 && <Text style={st.emptyTxt}>No flagged queries — the system is resolving well!</Text>}
              {flaggedQueries.map(fq => (
                <TouchableOpacity key={fq.id} activeOpacity={0.8}
                  style={{ backgroundColor: 'rgba(255,255,255,0.04)', borderRadius: 10, padding: 12, marginBottom: 8, borderWidth: 1, borderColor: flaggedSelected.has(fq.id) ? '#38bdf8' : 'rgba(255,255,255,0.08)' }}
                  onPress={() => setFlaggedSelected(prev => { const n = new Set(prev); n.has(fq.id) ? n.delete(fq.id) : n.add(fq.id); return n; })}>
                  <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <View style={{ flex: 1 }}>
                      <Text style={{ color: uiVar('adm-ink', '#fff'), fontSize: 14, fontWeight: '600' }}>"{fq.query}"</Text>
                      <View style={{ flexDirection: 'row', gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
                        <FlagReasons fq={fq} />
                        <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                          {fq.occurrence_count || 1}x searched
                        </Text>
                        {fq.confidence > 0 && (
                          <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
                            Conf: {Math.round(fq.confidence * 100)}%
                          </Text>
                        )}
                        {fq.query_type && (
                          <Text style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>{fq.query_type}</Text>
                        )}
                      </View>
                    </View>
                    <View style={{ width: 20, height: 20, borderRadius: 4, borderWidth: 1.5, borderColor: flaggedSelected.has(fq.id) ? '#38bdf8' : 'rgba(255,255,255,0.3)', backgroundColor: flaggedSelected.has(fq.id) ? '#38bdf8' : 'transparent', justifyContent: 'center', alignItems: 'center' }}>
                      {flaggedSelected.has(fq.id) && <Ionicons name="checkmark" size={12} color={uiVar('adm-ink', '#fff')} />}
                    </View>
                  </View>
                  {(fq.sport || fq.league || fq.team || fq.player || fq.market) && (
                    <View style={{ flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 4 }}>
                      {fq.sport && <Text style={{ color: uiVar('adm-accent', '#a78bfa'), fontSize: 11, backgroundColor: uiVar('adm-accent-wash-15', 'rgba(167,139,250,0.15)'), borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>{fq.sport}</Text>}
                      {fq.league && <Text style={{ color: '#3b82f6', fontSize: 11, backgroundColor: 'rgba(59,130,246,0.15)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>{fq.league}</Text>}
                      {fq.team && <Text style={{ color: '#10b981', fontSize: 11, backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>{fq.team}</Text>}
                      {fq.player && <Text style={{ color: '#f59e0b', fontSize: 11, backgroundColor: 'rgba(245,158,11,0.15)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>{fq.player}</Text>}
                      {fq.market && <Text style={{ color: '#ec4899', fontSize: 11, backgroundColor: 'rgba(236,72,153,0.15)', borderRadius: 4, paddingHorizontal: 6, paddingVertical: 1 }}>{fq.market}</Text>}
                    </View>
                  )}
                  <FlaggedCandidates fq={fq} onUse={(i) => handleUseCandidate(fq, i)}
                    busyIndex={candidateBusy?.id === fq.id ? candidateBusy.index : null} />
                  <View style={{ flexDirection: 'row', gap: 8, marginTop: 8 }}>
                    <TouchableOpacity style={{ backgroundColor: 'rgba(16,185,129,0.15)', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5, flexDirection: 'row', alignItems: 'center', gap: 4 }}
                      onPress={() => handlePromoteFlagged([fq.id])}>
                      <Ionicons name="arrow-up" size={12} color="#10b981" />
                      <Text style={{ color: '#10b981', fontSize: 11, fontWeight: '600' }}>Promote</Text>
                    </TouchableOpacity>
                    <TouchableOpacity style={{ backgroundColor: 'rgba(239,68,68,0.15)', borderRadius: 6, paddingHorizontal: 10, paddingVertical: 5, flexDirection: 'row', alignItems: 'center', gap: 4 }}
                      onPress={() => handleDismissFlagged([fq.id])}>
                      <Ionicons name="close" size={12} color="#ef4444" />
                      <Text style={{ color: '#ef4444', fontSize: 11, fontWeight: '600' }}>Dismiss</Text>
                    </TouchableOpacity>
                  </View>
                </TouchableOpacity>
              ))}
            </>
          )}
        </View>
      )}

      {activeView === 'aliases' && (
        <View>
          <Text style={st.title}>Market Aliases ({pendingAliases.length})</Text>
          <Text style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12, marginBottom: 12, lineHeight: 18 }}>
            Slang the AI mapped to the same market at least twice. Search uses an alias only after you confirm it, and only when no built-in keyword matches.
          </Text>
          <PendingAliasList aliases={pendingAliases} loading={aliasesLoading} onReview={handleReviewAlias} busyId={aliasBusy} />
        </View>
      )}

      {activeView === 'tester' && <QueryTester onSendToReview={(queryText) => {
        setHighlightQuery(queryText);
        setActiveView('review');
        fetchReviewQueue();
        setTimeout(() => setHighlightQuery(null), 6000);
      }} />}

      {activeView === 'add' && (
        <View>
          <Text style={st.title}>Add Resolution Manually</Text>
          <CorrectionForm form={addForm} setForm={setAddForm} dd={dropdownData} isAdd
            onSubmit={handleAdd} loading={addLoading} />
        </View>
      )}

      {activeView === 'metrics' && stats && (
        <MetricsView stats={stats} />
      )}
      <View style={{ height: 60 }} />
    </ScrollView>
  );
}

// ═══════════════════════════════════════════════════════════════════════
// Resolution Card
// ═══════════════════════════════════════════════════════════════════════